/**
 * A radial light that follows the cursor across a section.
 *
 * The centre is two CSS custom properties written straight from the pointer
 * handler. It deliberately does NOT set React state: the whole point is that
 * tracking the cursor costs no re-render.
 *
 * The listener goes on the parent via a ref callback, so the light follows the
 * cursor anywhere in the section - it is pointer-events-none and would never
 * receive events itself.
 */
import { useCallback, useRef, type CSSProperties } from "react";
import { useMotionLevel } from "./MotionZone";
import { useFinePointer } from "./useFinePointer";
import { cn } from "@/utils";

export function Spotlight({
  className,
  color = "rgba(18,190,203,0.16)",
}: {
  className?: string;
  color?: string;
}) {
  const level = useMotionLevel();
  const fine = useFinePointer();
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);
  const frame = useRef(0);

  const onPointerMove = useCallback((e: PointerEvent) => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const el = nodeRef.current;
      const host = hostRef.current;
      if (!el || !host) return;
      const r = host.getBoundingClientRect();
      if (!r.width || !r.height) return;
      el.style.setProperty("--sx", `${((e.clientX - r.left) / r.width) * 100}%`);
      el.style.setProperty("--sy", `${((e.clientY - r.top) / r.height) * 100}%`);
    });
  }, []);

  /* A ref callback rather than useEffect: it fires on both attach and detach,
     so the listener is bound and unbound with the node itself. */
  const attach = useCallback(
    (node: HTMLDivElement | null) => {
      if (hostRef.current) {
        hostRef.current.removeEventListener("pointermove", onPointerMove);
        hostRef.current = null;
      }
      if (frame.current) {
        cancelAnimationFrame(frame.current);
        frame.current = 0;
      }
      nodeRef.current = node;
      const host = node?.parentElement ?? null;
      if (host) {
        host.addEventListener("pointermove", onPointerMove, { passive: true });
        hostRef.current = host;
      }
    },
    [onPointerMove],
  );

  if (level === "quiet" || !fine) return null;

  return (
    <div
      ref={attach}
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0", className)}
      style={
        {
          background: `radial-gradient(28rem 28rem at var(--sx) var(--sy), ${color}, transparent 70%)`,
          "--sx": "50%",
          "--sy": "50%",
        } as CSSProperties
      }
    />
  );
}
